import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const REMARK_PLUGINS = [remarkGfm];

const LIST_CLASS = 'mb-m pl-2xl';
const CELL_CLASS = 'border border-gray-6 px-m py-1.5 text-left text-body-md';

function buildComponents(headingClass: string): Components {
  return {
    p: ({ node, ...props }) => <p className="mb-m" {...props} />,
    h1: ({ node, ...props }) => <h1 className={headingClass} {...props} />,
    h2: ({ node, ...props }) => <h2 className={headingClass} {...props} />,
    h3: ({ node, ...props }) => <h3 className={headingClass} {...props} />,
    h4: ({ node, ...props }) => <h4 className={headingClass} {...props} />,
    ul: ({ node, ...props }) => <ul className={LIST_CLASS} {...props} />,
    ol: ({ node, ...props }) => <ol className={LIST_CLASS} {...props} />,
    li: ({ node, ...props }) => <li className="my-xs" {...props} />,
    code: ({ node, ...props }) => (
      <code
        className="rounded-xs bg-background-main px-1.5 py-px text-[14px]"
        {...props}
      />
    ),
    pre: ({ node, ...props }) => (
      <pre
        className="mb-m overflow-x-auto rounded-sm bg-background-main p-m [&_code]:bg-transparent [&_code]:p-0"
        {...props}
      />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        className="mb-m border-l-2 border-gray-4 pl-m text-gray"
        {...props}
      />
    ),
    table: ({ node, ...props }) => (
      <table
        className="mb-m block max-w-full border-collapse overflow-x-auto"
        {...props}
      />
    ),
    th: ({ node, ...props }) => <th className={CELL_CLASS} {...props} />,
    td: ({ node, ...props }) => <td className={CELL_CLASS} {...props} />,
    a: ({ node, ...props }) => (
      <a
        className="text-purple-default underline underline-offset-2"
        {...props}
      />
    ),
  };
}

const COMPONENTS = buildComponents('mt-xl mb-m text-h2 font-bold');
const COMPACT_COMPONENTS = buildComponents('mt-l mb-s text-body-lg font-bold');

// Deliberately unfiltered: users must see exactly what the model emits,
// including its knowledge-base citation links (relative paths that 404 in
// this app).
export default function AnswerMarkdown({
  children,
  compact = false,
}: {
  children: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`w-full min-w-0 text-gray-text *:first:mt-0 *:last:mb-0 ${
        compact ?
          'text-body-md font-medium'
        : 'text-body-lg max-md:text-body-md'
      }`}
    >
      <Markdown
        remarkPlugins={REMARK_PLUGINS}
        components={compact ? COMPACT_COMPONENTS : COMPONENTS}
      >
        {children}
      </Markdown>
    </div>
  );
}
